import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import { checkPin, getProfile } from "@/api/endpoints/auth";
import { API_ERROR_CODES, ApiError } from "@/api/client";
import { closeWebview } from "@/app/back";
import { notify } from "@/app/notify";
import { ROUTES } from "@/app/routes";
import { PinView, type PinViewHandle } from "@/components/ui/PinView";
import { ScreenHeader } from "@/components/ui/ScreenHeader";
import { ScreenMessage } from "@/components/ui/ScreenMessage";

import { describeFailure } from "@/lib/failure";

const WRONG_PIN_MESSAGE = "รหัส PIN ไม่ถูกต้อง";

function isWrongPinError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const pinBusinessCode =
    typeof error.businessCode === "string" &&
    error.businessCode.startsWith("PIN_");
  const pinText =
    typeof error.message === "string" &&
    error.message.toLowerCase().includes("pin") &&
    error.message.includes("ไม่ถูกต้อง");
  const pinHttp400 =
    error.code === API_ERROR_CODES.http &&
    error.httpStatus === 400 &&
    pinText;
  return pinBusinessCode || pinHttp400;
}

export default function InputPinPage() {
  const navigate = useNavigate();
  const pinRef = useRef<PinViewHandle>(null);
  const verifyingRef = useRef(false);
  const me = useQuery({ queryKey: ["auth", "me"], queryFn: () => getProfile() });
  const verify = useMutation({
    mutationFn: (pin: string) => {
      if (me.data === undefined) {
        throw new Error("Cannot verify a PIN before the customer loads.");
      }
      return checkPin({ guid: me.data.guid, pin });
    },
    onSuccess: () => {
      void navigate(ROUTES.settings.accountDocumentAddress, { replace: true });
    },
    onError: (error) => {
      if (isWrongPinError(error)) {
        notify("error", WRONG_PIN_MESSAGE);
        pinRef.current?.clearAll();
        return;
      }
      notify("error", "กรุณาลองใหม่อีกครั้ง");
      pinRef.current?.clearAll();
    },
    onSettled: () => {
      verifyingRef.current = false;
    },
  });

  const onPinChange = (pin: string) => {
    if (pin.length < 6 || verifyingRef.current || me.data === undefined) {
      return;
    }
    verifyingRef.current = true;
    verify.mutate(pin);
  };
  const failure = me.isError ? describeFailure(me.error) : null;
  if (failure !== null) {
    return (
      <>
        <ScreenHeader title="กรอก PIN Code" />
        <main className="safe-bottom flex-1 bg-white px-4 pt-6 tracking-[-0.011em] my-16">
          <ScreenMessage
            title={failure.title}
            description={failure.description}
            actionLabel={failure.action === "retry" ? "ลองใหม่" : "กลับ"}
            onAction={() => {
              if (failure.action === "retry") {
                void me.refetch();
                return;
              }
              closeWebview();
            }}
          />
        </main>
      </>
    );
  }

  return (
    <>
      <ScreenHeader title="กรอก PIN Code" />
      <main className="relative flex flex-1 flex-col items-center bg-white px-4 tracking-[-0.011em] my-16">
        <PinView
          ref={pinRef}
          disabled={me.data === undefined || verify.isPending}
          onChange={onPinChange}
        />
      </main>
    </>
  );
}
